/**
 * Late-fee + reminder policy input (PRD v2 §2.2). The DB holds the same bounds as CHECKs;
 * these exist so a shop gets a sentence instead of a constraint-violation stack trace.
 */
import { z } from 'zod';
import { idempotency } from './spine';

export const invoicePolicyInput = idempotency.extend({
  lateFeeEnabled: z.boolean().optional(),
  lateFeeKind: z.enum(['flat', 'percent']).optional(),
  lateFeeFlatCents: z.coerce.number().int().min(0).max(100_000_000).optional(),
  /** 10000 bps = 100%. A fee larger than the debt is a typo, never an intent. */
  lateFeePercentBps: z.coerce.number().int().min(0).max(10_000).optional(),
  lateFeeGraceDays: z.coerce.number().int().min(0).max(90).optional(),
  lateFeeIntervalDays: z.coerce.number().int().min(0).max(365).optional(),
  lateFeeMaxCount: z.coerce.number().int().min(1).max(24).optional(),
  lateFeeMinBalanceCents: z.coerce.number().int().min(0).max(100_000_000).optional(),
  remindersEnabled: z.boolean().optional(),
  reminderOffsetDays: z
    .array(z.coerce.number().int().min(-30).max(365))
    .min(1)
    .max(6)
    .refine((days) => new Set(days).size === days.length, {
      message: 'Two reminders on the same day would send the same email twice',
    })
    .optional(),
});
export type InvoicePolicyInput = z.infer<typeof invoicePolicyInput>;

export const waiveLateFeeInput = idempotency.extend({
  lateFeeId: z.string().uuid(),
  reason: z.string().trim().max(500).optional().or(z.literal('')),
});
export type WaiveLateFeeInput = z.infer<typeof waiveLateFeeInput>;

/**
 * Running the sweep by hand. `dryRun` is the default and the point: an owner should be able
 * to ask "what happens tonight" and get the answer without it having happened.
 */
export const billingSweepInput = idempotency.extend({
  dryRun: z.boolean().default(true),
});
export type BillingSweepInput = z.infer<typeof billingSweepInput>;
